/* eslint-disable */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { addDays, differenceInCalendarDays } from 'date-fns'
import { debounce, throttle } from 'lodash'
import ResizeObserver from 'resize-observer-polyfill'
import CenterText, { CenterTextPrint } from '../../../components/Calendar/BookingCalendar/CenterText'
import CalendarLoader from '../../../components/Loader/CalendarLoader'
import { getDevicePixelRatio } from '../../../components/MediaQuery'
import { TranslatePhrase } from '../../../i18n'
import { IRoomWithEvents } from '../../../reducers/bookingCalendar/selectors/rooms'
import { getDimensions, IDimensions } from '../../../utils/Canvas'
import clamp from '../../../utils/clamp'
import { useIsMobile } from '../../../components/MediaQuery/Mobile'
import HeaderCanvas from '../../Canvas/HeaderCanvas'
import RoomCanvas from '../../Canvas/RoomCanvas'
import FilterDrawer from '../FilterDrawer'
import ToolBar from './Toolbar/index'
import CalendarCanvasWrapper from './CalendarCanvasWrapper'
import { Mixpanel } from '../../../utils/Mixpanel'
import { CalendarActions } from '../../../reducers/bookingCalendar/actionNames'
import { useDispatch, useSelector } from 'react-redux'
import { getUser, getUserId } from '../../../reducers/userAccount/selectors/userAccount'

export const dim: ICalendarDimensions = {
  EVENT_DIAMOND_SLOPE: 30,
  EVENT_FONT_CHAR_WIDTH: 12,
  EVENT_FONT_SIZE: 16,
  EVENT_GAP: 4,
  EVENT_HEIGHT: 50,
  EVENT_RECT_ROUND: 5,
  EVENT_STROKE_WIDTH: 2,
  GRID_BORDER_WIDTH: 1,
  GRID_DIAGONAL_LINE_SPACING: 12,
  GRID_DIAGONAL_LINE_THICKNESS: 2,
  GRID_DIAGONAL_LINE_WIDTH: 2,
  GRID_HEIGHT: 60,
  GRID_WIDTH: 56,
  HEADER_DAY_FONT_SIZE: 15,
  HEADER_HEIGHT: 46,
  HEADER_LABEL_FONT_SIZE: 11,
  HEADER_MARKED_DAY_BORDER: 3,
  HEADER_SELECTED_DAY_HEIGHT: 36,
  HEADER_SELECTED_DAY_WIDTH: 44,
  HEADER_TEXTS_PADDING: 3,
  PIXEL_RATIO: 1,
  ROOM_FONT_SIZE: 14,
  ROOM_INTEGRATION_ICON_OVERLAP: 2,
  ROOM_INTEGRATION_ICON_SIZE: 15,
  ROOM_LINE_HEIGHT: 13.2,
  ROOM_PADDING_BOTTOM: 6,
  ROOM_PADDING_LEFT: 9,
  ROOM_PADDING_RIGHT: 10,
  ROOM_PADDING_TOP: 9,
  ROOM_SINGLE_LINE_PADDING_TOP: -2,
  ROOM_SIZE_FONT_SIZE: 15,
  ROOM_SIZE_TEXT_OFFSET: 12,
  ROOM_TRIANGLE_SIZE: 12,
  ROOM_TYPE_ICON_SIZE: 18,
  ROOM_WIDTH: 220,
  ROOM_WIDTH_MOBILE: 125,
  ROOM_WRAP_FROM_RIGHT: 6,
  SUMMARY_FONT_SIZE: 12,
  SUMMARY_HEIGHT: 60,
  SUMMARY_ICON_SIZE: 12,
}

const Calendar = ({
  rooms,
  selectedDays,
  size = 0.85,
  roomsAreLoading,
  eventsFullRange,
  errMessage,
  minDate,
  maxDate,
  changeDate,
  manualDate = null,
  diff,
  setDiff,
  summary,
  setIsDragging,
  setIsScrolling,
  nonScrolling = false,
  withRooms = true,
  withToolbar = true,
  storeScrollTop,
  storedScrollTop,
  storedFromDate,
  printCalendar,
  isSomeLoaded,
}: ICalendarProps & ICalendarDispatch) => {
  const dispatch = useDispatch()
  const isMobile = useIsMobile()
  const [constants, setDimensions] = useState(
    getDimensions<ICalendarDimensions>(dim, size, isMobile),
  )
  const [dpConstants, setDpConstants] = useState(
    getDimensions<ICalendarDimensions>(
      dim,
      size,
      isMobile,
      printCalendar ? 1 : getDevicePixelRatio(),
    ),
  )

  const daysBetween = useMemo(
    () => differenceInCalendarDays(maxDate, minDate) + 1,
    [maxDate, minDate],
  )
  const scrollWidth = useMemo(() => daysBetween * constants.GRID_WIDTH, [
    daysBetween,
    constants,
  ])
  const scrollHeight = useMemo(
    () =>
      clamp(
        rooms.length * constants.GRID_HEIGHT +
          (summary ? constants.SUMMARY_HEIGHT : 0),
        1,
      ),
    [rooms, constants, summary],
  )
  const [width, setWidth] = useState(0)
  const [height, setHeight] = useState(0)
  const [maxHeight, setMaxHeight] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(
    nonScrolling
      ? 0
      : differenceInCalendarDays(
          storedFromDate ? storedFromDate : new Date(),
          minDate,
        ) * constants.GRID_WIDTH,
  )
  const [scrollTop, setScrollTop] = useState(storedScrollTop || 0)
  const [wrapperRef, setWrapperRef] = useState(null)
  const [scrollbarsRef, setScrollbarsRef] = useState(null)

  const isDraggingCallback = useCallback(
    toggle => () => setIsDragging && setIsDragging(toggle),
    [setIsDragging],
  )
  const isScrollingCallback = useCallback(
    toggle => () => setIsScrolling && setIsScrolling(toggle),
    [setIsScrolling],
  )

  const startDay = useMemo(
    () => addDays(minDate, Math.floor(scrollLeft / constants.GRID_WIDTH)),
    [scrollLeft, constants],
  )
  const startRoomIdx = useMemo(
    () => Math.floor(scrollTop / constants.GRID_HEIGHT),
    [scrollTop, constants],
  )

  useEffect(() => {
    dispatch({ type: CalendarActions.OpenOnbordingModal })
  }, [])

  useEffect(() => {
    if (scrollbarsRef) {
      scrollbarsRef.scrollLeft(scrollLeft)
    }
  }, [scrollbarsRef])

  useEffect(() => {
    setTimeout(function() {
      Mixpanel.track('Calendar View', [])
    }, 2000)
  }, [])

  const setWidthHeight = () => {
    if (wrapperRef) {
      const rect = wrapperRef.getBoundingClientRect()
      setWidth(
        clamp(
          nonScrolling ? Number.POSITIVE_INFINITY : Math.round(rect.width),
          null,
          scrollWidth + constants.ROOM_WIDTH,
        ),
      )
      setMaxHeight(rect.height)
      setHeight(
        clamp(
          nonScrolling ? Number.POSITIVE_INFINITY : Math.round(rect.height),
          nonScrolling ? 400 : undefined,
          scrollHeight + constants.HEADER_HEIGHT,
        ),
      )
    }
  }

  useEffect(() => {
    setDiff &&
      setDiff(Math.floor((width - constants.ROOM_WIDTH) / constants.GRID_WIDTH))
  }, [width, constants])

  useEffect(() => {
    const debounceChanges = debounce(setWidthHeight, 100)
    if (wrapperRef) {
      setWidthHeight()
      const ro = new ResizeObserver(() => {
        debounceChanges()
      })

      ro.observe(wrapperRef)
      return () => {
        ro.disconnect()
      }
    }
  }, [wrapperRef, scrollWidth, scrollHeight])

  useEffect(() => {
    setDimensions(getDimensions<ICalendarDimensions>(dim, size, isMobile))
    setDpConstants(
      getDimensions<ICalendarDimensions>(
        dim,
        size,
        isMobile,
        printCalendar ? 1 : getDevicePixelRatio(),
      ),
    )
  }, [size, isMobile, width])

  const changeDateThrottled = useCallback(
    throttle((startDay: Date) => {
      changeDate && changeDate(startDay, diff)
    }, 100),
    [changeDate, diff],
  )
  const throttledScrollListener = useCallback(
    throttle((e: any) => {
      setScrollTop(clamp(e.target.scrollTop, 0))
      setScrollLeft(clamp(e.target.scrollLeft, 0))
    }, 16),
    [],
  )

  useEffect(() => {
    const newScrollLeft =
      differenceInCalendarDays(startDay, minDate) * constants.GRID_WIDTH
    changeDateThrottled.cancel()
    throttledScrollListener.cancel()
    if (scrollbarsRef && !nonScrolling) {
      setScrollLeft(newScrollLeft)
      scrollbarsRef.scrollLeft(newScrollLeft)
    }
    if (nonScrolling) {
      setScrollLeft(0)
    }
  }, [minDate])

  useEffect(() => {
    if (scrollbarsRef) {
      scrollbarsRef.scrollTop(scrollTop)
    }
  }, [scrollbarsRef])

  useEffect(() => {
    if (manualDate && scrollbarsRef) {
      scrollbarsRef.scrollLeft(
        differenceInCalendarDays(manualDate, minDate) * constants.GRID_WIDTH +
          1,
      )
    }
  }, [manualDate, scrollbarsRef])

  useEffect(() => {
    changeDateThrottled(startDay)
  }, [+startDay, changeDateThrottled])

  const changeScrollTopThrottled = useCallback(
    throttle((scrollTop: number) => {
      storeScrollTop && storeScrollTop(scrollTop)
    }, 150),
    [storeScrollTop],
  )

  useEffect(() => {
    changeScrollTopThrottled(scrollTop)
  }, [scrollTop, changeScrollTopThrottled])

  if (errMessage !== '') {
    return <CenterText>{errMessage}</CenterText>
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          height: '100%',
          flexGrow: 1,
          minHeight: 0,
          position: 'relative',
          width: '100%',
        }}
        ref={setWrapperRef}
      >
        {!isSomeLoaded && !printCalendar ? (
          <CalendarLoader
            style={{ height: `${maxHeight - constants.HEADER_HEIGHT}px` }}
          />
        ) : (
          <>
            {width > 0 && height > 0 && (
              <>
                {withRooms && (
                  <FilterDrawer
                    size={size}
                    style={{
                      height: `${constants.HEADER_HEIGHT}px`,
                      width: `${constants.ROOM_WIDTH}px`,
                    }}
                    isLoading={!eventsFullRange}
                    isFilterable={eventsFullRange}
                  />
                )}
                <>
                  {rooms.length === 0 &&
                    !roomsAreLoading &&
                    (printCalendar ? (
                      <CenterTextPrint>
                        <TranslatePhrase i18nKey={'search_not_found'} />
                      </CenterTextPrint>
                    ) : (
                      <CenterText size={size}>
                        <TranslatePhrase i18nKey={'search_not_found'} />
                      </CenterText>
                    ))}
                  <CalendarCanvasWrapper
                    startDay={nonScrolling ? minDate : startDay}
                    minDate={minDate}
                    scrollLeft={scrollLeft}
                    scrollTop={scrollTop}
                    width={width - constants.ROOM_WIDTH}
                    height={height - constants.HEADER_HEIGHT}
                    rooms={rooms}
                    selectedDays={selectedDays}
                    constants={constants}
                    dpConstants={dpConstants}
                    startRoomIdx={startRoomIdx}
                    scrollHeight={scrollHeight}
                    scrollWidth={scrollWidth}
                    scrollbarsRef={setScrollbarsRef}
                    onScroll={throttledScrollListener}
                    style={{
                      cursor: 'pointer',
                      left: withRooms ? `${constants.ROOM_WIDTH}px` : 0,
                      position: 'absolute',
                      top: `${constants.HEADER_HEIGHT}px`,
                    }}
                    toggleDragging={isDraggingCallback}
                    toggleIsScrolling={isScrollingCallback}
                    withSummary={summary}
                  />
                  {withRooms && (
                    <RoomCanvas
                      startRoomIdx={startRoomIdx}
                      scrollTop={scrollTop}
                      width={constants.ROOM_WIDTH}
                      height={height - constants.HEADER_HEIGHT}
                      rooms={rooms}
                      constants={constants}
                      dpConstants={dpConstants}
                      size={size}
                      style={{
                        cursor: 'pointer',
                        left: `0px`,
                        position: 'absolute',
                        top: `${constants.HEADER_HEIGHT}px`,
                      }}
                      withSummary={summary}
                    />
                  )}
                </>
                <HeaderCanvas
                  startDay={nonScrolling ? minDate : startDay}
                  minDate={minDate}
                  scrollLeft={scrollLeft}
                  width={width - constants.ROOM_WIDTH}
                  height={constants.HEADER_HEIGHT}
                  selectedDays={selectedDays}
                  constants={constants}
                  dpConstants={dpConstants}
                  size={size}
                  style={{
                    cursor: 'pointer',
                    left: withRooms ? `${constants.ROOM_WIDTH}px` : 0,
                    position: 'absolute',
                    top: '0px',
                  }}
                  refHeight={height}
                />
              </>
            )}
          </>
        )}
      </div>
      {withToolbar && isSomeLoaded && !printCalendar && <ToolBar size={size} />}
    </>
  )
}

export default Calendar

export interface ICalendarProps {
  selectedDays?: Date[]
  storedScrollTop?: number
  rooms: IRoomWithEvents[]
  errMessage: string
  size?: number
  summary?: boolean
  eventsAreLoading?: boolean
  eventsFullRange?: boolean
  roomsAreLoading?: boolean
  minDate?: Date
  maxDate?: Date
  changeDate?: (date: Date, daysCount: number) => void
  manualDate?: Date | null
  diff?: number
  setDiff?: (diff: number) => void
  nonScrolling?: boolean
  withToolbar?: boolean
  withRooms?: boolean
  storedFromDate?: Date
  printCalendar?: boolean
  isSomeLoaded?: boolean
}

export interface ICalendarDispatch {
  loadRooms?: (withRooms, dates?: IDateRange) => void
  storeScrollTop?: (scrollTop: number) => void
  setIsDragging?: (toggle: boolean) => void
  setIsScrolling?: (toggle: boolean) => void
}

interface IDateRange {
  startDate: Date
  finishDate: Date
}

export interface IWithSize {
  size?: number
}

export interface ICalendarDimensions extends IDimensions {
  EVENT_DIAMOND_SLOPE: number
  EVENT_FONT_CHAR_WIDTH: number
  EVENT_FONT_SIZE: number
  EVENT_GAP: number
  EVENT_HEIGHT: number
  EVENT_RECT_ROUND: number
  EVENT_STROKE_WIDTH: number
  SUMMARY_FONT_SIZE: number
  SUMMARY_HEIGHT: number
  SUMMARY_ICON_SIZE: number
}
